import { Fragment, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  CloseIcon,
  HelpIcon,
  CollapseIcon,
  ExpandIcon,
  NextIcon,
  PreviousIcon,
  SaveIcon,
} from '@platform/ui-kit';
import {
  api,
  RECOMMENDATIONS,
  type AprApplication,
  type AprDispositionSummary,
  type AprLicensingForApp,
} from '../../api';
import { describeError } from '../admin/useAsyncData';
import { formatCurrency } from './shared';

/**
 * The Application Master Data detail drawer, rebuilt to APR's layout.
 *
 * Two panes side by side, as APR has them:
 *
 *   LEFT   "Application Details" — a two-column grid of labelled fields, read-only
 *          until Edit is pressed, then the rationalization toggles.
 *   RIGHT  "Analysis" — one card per downstream module.
 *
 * Above both, a FAMILY strip: when an application belongs to a parent/child group it
 * can be stepped through without closing the drawer, which is the point of grouping.
 *
 * Measurements are APR's own, from its `ApplicationDetailDrawer.tsx`:
 *   label  12px · 600 · #555      read view  7px 9px · 13px · radius 6 · #f8fafc
 *   card   1px #e6e6e6 · radius 10 · 14px padding · shadow 0 1px 2px rgba(0,0,0,.04)
 *
 * The Analysis cards are fed by modules that have not been migrated. They render as
 * explicit "not assessed" panels rather than being hidden: a missing section reads as
 * a bug, an empty one reads as work still to come.
 */

const label = (text: string) => (
  <Typography
    component="label"
    sx={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555555', mb: '4px' }}
  >
    {text}
  </Typography>
);

/**
 * The same label with an information icon after it.
 *
 * Reuses the 16px `HelpIcon` in a `Tooltip` that `FlagToggle` already uses further down this
 * file, so the two ways of asking "what is this?" on this panel look identical.
 */
const labelWithInfo = (text: string, info: string) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '4px' }}>
    <Typography
      component="label"
      sx={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555555' }}
    >
      {text}
    </Typography>
    <Tooltip
      arrow
      placement="right"
      componentsProps={{ tooltip: { sx: { maxWidth: 340, fontSize: 12, lineHeight: 1.5 } } }}
      title={<Box sx={{ p: 0.5 }}>{info}</Box>}
    >
      <Box component="span" sx={{ display: 'inline-flex', color: 'primary.main', cursor: 'help' }}>
        <HelpIcon sx={{ fontSize: 16 }} />
      </Box>
    </Tooltip>
  </Box>
);

/** APR's read-only field box: a filled, bordered value that is clearly not an input. */
function ReadView({ value }: { value: unknown }) {
  const text = value === null || value === undefined || value === '' ? '—' : String(value);
  return (
    <Box
      sx={{
        px: '9px',
        py: '7px',
        fontSize: 13,
        borderRadius: '6px',
        minHeight: 32,
        display: 'flex',
        alignItems: 'center',
        bgcolor: (t) => (t.palette.mode === 'dark' ? 'action.hover' : '#f8fafc'),
        border: '1.25px solid',
        borderColor: (t) => (t.palette.mode === 'dark' ? 'divider' : '#eef2f6'),
        color: (t) => (t.palette.mode === 'dark' ? 'text.primary' : '#334155'),
      }}
    >
      {text}
    </Box>
  );
}

const EDIT_FIELD_SX = {
  '& .MuiOutlinedInput-root': { borderRadius: '6px', bgcolor: 'background.paper' },
  '& .MuiOutlinedInput-input': { padding: '7px 9px', fontSize: 13 },
  '& .MuiOutlinedInput-notchedOutline': { borderWidth: '1.25px', borderColor: '#d0d0d0' },
} as const;

/** APR's switch: 40×22 track, 18px knob, #cbd5e1 off / rgb(27,122,43) on. */
const SWITCH_SX = {
  width: 40,
  height: 22,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: '2px',
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: '#fff',
      '& + .MuiSwitch-track': { backgroundColor: 'rgb(27, 122, 43)', opacity: 1 },
    },
  },
  '& .MuiSwitch-thumb': { width: 18, height: 18, boxShadow: 'none' },
  '& .MuiSwitch-track': { borderRadius: '12px', backgroundColor: '#cbd5e1', opacity: 1 },
} as const;

/**
 * One APR-relevance flag: switch, name, an info tooltip, and a hint beneath.
 *
 * The two flags this renders decide different things — one scopes rationalization, the
 * other feeds the licensing workstream — and the source's own comment notes they are
 * "routinely confused". So each carries a tooltip saying what it does and what it does
 * NOT do, and that copy is the source drawer's word for word.
 */
function FlagToggle({
  on,
  disabled,
  onChange,
  label: name,
  hint,
  info,
}: {
  on: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint?: string;
  info?: React.ReactNode;
}) {
  return (
    <Box sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Switch
          sx={SWITCH_SX}
          checked={on}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          inputProps={{ 'aria-label': name }}
        />
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{name}</Typography>
        {info && (
          <Tooltip
            arrow
            placement="right"
            componentsProps={{ tooltip: { sx: { maxWidth: 380, fontSize: 12, lineHeight: 1.5 } } }}
            title={<Box sx={{ p: 0.5 }}>{info}</Box>}
          >
            <Box component="span" sx={{ display: 'inline-flex', color: 'primary.main', cursor: 'help' }}>
              <HelpIcon sx={{ fontSize: 16 }} />
            </Box>
          </Tooltip>
        )}
      </Box>
      {hint && (
        <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25, ml: '58px', lineHeight: 1.4 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        border: '1.25px solid',
        borderColor: (t) => (t.palette.mode === 'dark' ? 'divider' : '#e6e6e6'),
        borderRadius: '10px',
        p: '14px',
        bgcolor: 'background.paper',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      {children}
    </Box>
  );
}

function CardHead({ title, right }: { title: string; right?: React.ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#333333' }}>{title}</Typography>
      {right}
    </Stack>
  );
}

/**
 * The Disposition 360 presentational helpers, reproduced from the source drawer
 * (`apr_template/src/components/ApplicationDetailDrawer.tsx`) — same metrics, same
 * colours, same wording. They are plain elements rather than MUI so the card matches the
 * source pixel for pixel; the Card/CardHead shell around them is already APR's.
 */
const DispChip = ({ label, color, solid }: { label: string; color: string; solid?: boolean }) => (
  <span style={{
    padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    background: solid ? color : `${color}22`, color: solid ? '#fff' : color,
  }}>{label}</span>
);

/** One signal — Model, Q&A, Suggested, Manual — as a small grey capsule. */
const SignalMini = ({ label, value, color, mutedText }: {
  label: string; value: string | null; color?: string; muted?: boolean; mutedText?: string;
}) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 8, background: '#f4f4f6', fontSize: 11 }}>
    <span style={{ color: '#999', fontWeight: 600 }}>{label}</span>
    {value
      ? <span style={{ color: color || '#333', fontWeight: 700 }}>{value}</span>
      : <span style={{ color: '#bbb', fontStyle: 'italic' }}>{mutedText || '—'}</span>}
  </span>
);

/**
 * The threshold bar, with a marker per scored signal.
 *
 * The bands are equal-width regardless of their numeric span — the source draws them with
 * `flex: 1` — while a marker is positioned by SCORE across the 1–5 range. Both behaviours
 * are the source's and are reproduced rather than corrected.
 */
const ScoreScale = ({ thresholds, markers }: {
  thresholds: Array<{ label: string; min: number; max: number; color: string }>;
  markers: Array<{ score: number | null; label: string }>;
}) => (
  <div style={{ position: 'relative', paddingTop: 8 }}>
    <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden' }}>
      {thresholds.map((t) => (
        <div key={t.label} style={{ flex: 1, background: t.color }} title={`${t.label}: ${t.min}–${t.max}`} />
      ))}
    </div>
    {markers.filter((m) => m.score != null).map((m, i) => {
      const pct = Math.max(0, Math.min(100, (((m.score as number) - 1) / 4) * 100));
      return (
        <div key={i} title={`${m.label}: ${(m.score as number).toFixed(2)}`}
          style={{ position: 'absolute', left: `${pct}%`, top: 0, transform: 'translateX(-50%)' }}>
          <div style={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '8px solid #111' }} />
        </div>
      );
    })}
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#aaa', marginTop: 2 }}>
      <span>Retire</span><span>Re-Platform</span><span>Re-Design</span><span>Retain</span>
    </div>
  </div>
);

/** The headline on the Disposition 360 card: do the signals agree? */
const CONVERGENCE_META: Record<string, { color: string; label: string }> = {
  converged: { color: '#1b7a2b', label: 'Signals aligned' },
  divergent: { color: '#d97706', label: 'Signals diverge' },
  none: { color: '#6b7280', label: 'Not assessed' },
};

/** An Analysis card whose module has not been migrated. */
function AwaitingCard({ title, note }: { title: string; note: string }) {
  return (
    <Card>
      <CardHead title={title} right={<Box sx={NEUTRAL_CHIP_SX}>Not assessed</Box>} />
      <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{note}</Typography>
    </Card>
  );
}

const SECTION_TITLE = { fontSize: 14, fontWeight: 700, color: '#222222' } as const;

/** APR's neutral status chip: 11px/700 on a 13% tint of its own colour. */
const NEUTRAL_CHIP_SX = {
  px: '8px',
  py: '2px',
  fontSize: 11,
  fontWeight: 700,
  borderRadius: '12px',
  color: 'rgb(107, 114, 128)',
  bgcolor: 'rgba(107, 114, 128, 0.133)',
  whiteSpace: 'nowrap',
} as const;

/**
 * The header's four controls, to APR's measurements: 36px square (or 0 14px for the
 * text button), radius 9, a 1.5px border and a tinted fill per role.
 */
const CONTROL_BASE = {
  height: 36,
  borderRadius: '9px',
  border: '1.5px solid',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  font: 'inherit',
} as const;

const BACK_BUTTON_SX = {
  ...CONTROL_BASE,
  px: '14px',
  gap: '4px',
  borderColor: '#cbd5e1',
  bgcolor: '#f1f5f9',
  color: '#334155',
  fontSize: 13,
  fontWeight: 700,
} as const;

const ICON_BUTTON_SX = {
  ...CONTROL_BASE,
  width: 36,
  borderColor: '#cbd5e1',
  bgcolor: '#f1f5f9',
  color: '#475569',
} as const;

const SAVE_BUTTON_SX = {
  ...CONTROL_BASE,
  width: 36,
  borderColor: '#16a34a',
  bgcolor: '#dcfce7',
  color: '#15803d',
} as const;

const CLOSE_BUTTON_SX = {
  ...CONTROL_BASE,
  width: 36,
  borderColor: '#dc2626',
  bgcolor: '#fee2e2',
  color: '#b91c1c',
} as const;

/** The header pills, same as the grid's. */
const PILL_BASE = {
  px: '8px',
  py: '2px',
  borderRadius: '12px',
  whiteSpace: 'nowrap',
  lineHeight: 1.4,
  flexShrink: 0,
} as const;

const PARENT_PILL_SX = {
  ...PILL_BASE,
  fontSize: 11,
  fontWeight: 600,
  bgcolor: '#1976d2',
  color: '#fff',
} as const;

const CHILD_PILL_SX = {
  ...PILL_BASE,
  fontSize: 10,
  fontWeight: 500,
  bgcolor: '#e3f2fd',
  color: '#1565c0',
} as const;

interface DetailField {
  key: keyof AprApplication;
  label: string;
  /** Spans both columns, for free text where a half-width box would truncate it. */
  full?: boolean;
  multiline?: boolean;
  /**
   * What this ownership means, behind an information icon beside the label.
   *
   * Owner-directed. The five owner types are no longer just contact details: each one is a
   * respondent, and which questions reach whom is decided by the aspect on a dimension. Two
   * of them are easy to confuse — the Application Owner is the architect, the Technical Owner
   * is who runs it — and getting that wrong sends a security questionnaire to the wrong desk.
   */
  info?: string;
}

/**
 * The field grid, in chapters — owner-directed, 2026-08-24.
 *
 * It was one flat run of 21 fields in APR's order, which put the four owner fields in the
 * middle of the list with nothing saying they belonged together. The ORDER of the fields
 * APR ships is unchanged inside each chapter; the chapters group what was already there and
 * carry the six new ownership fields (migration 0082).
 *
 * `integration_dependencies` and `current_operating` are not listed — owner-directed, he does
 * not want to see them. Both COLUMNS remain, and both are still read and written by the
 * server; only the panel stops showing them. `current_operating` has never held a value in
 * either database anyway, and APR's own code disagrees with itself about what it means.
 *
 * Executive ownership is its own chapter rather than the tail of Ownership Details: the
 * accountable executive is a different question from who runs the thing day to day.
 */
const GROUPS: { title: string; fields: DetailField[] }[] = [
  {
    title: 'Basic Data',
    fields: [
      { key: 'name', label: 'Application Name' },
      { key: 'application_type', label: 'Application Type' },
      { key: 'business_function_domain', label: 'Business Function / Domain' },
      { key: 'l1_business_functional_area', label: 'L1 - Business Functional Area' },
      { key: 'l2_capability_function', label: 'L2 - Capability Function' },
      { key: 'technology_stack', label: 'Technology Stack' },
      { key: 'it_hardware_infrastructure', label: 'IT Hardware / Infrastructure' },
    ],
  },
  {
    title: 'Ownership Details',
    fields: [
      {
        key: 'application_owner',
        label: 'Application Owner',
        info: 'Accountable for the application as a system — usually the architect. Answers the Technical questions.',
      },
      { key: 'app_owner_email', label: 'App Owner Email' },
      {
        key: 'technical_owner_custodian',
        label: 'Technical Owner / Custodian',
        info: 'Runs and maintains it day to day — platform, patching, availability. Answers the Operational questions.',
      },
      { key: 'to_email', label: 'Technical Owner Email' },
      {
        key: 'business_process_owner',
        label: 'Business Process Owner (BPO)',
        info: 'Owns the business process the application serves and signs off changes to it. Answers the Business questions.',
      },
      { key: 'bpo_email', label: 'BPO Email' },
      {
        key: 'security_owner',
        label: 'Security Owner',
        info: "Answers for the application's security posture, controls and compliance — the Security questions.",
      },
      { key: 'security_owner_email', label: 'Security Owner Email' },
    ],
  },
  {
    title: 'Executive Ownership',
    fields: [
      {
        key: 'executive_owner',
        label: 'Executive Owner',
        info: "The accountable executive, also called the sponsor. Answers for the application's strategic future and its funding — the Strategy questions.",
      },
      { key: 'executive_owner_email', label: 'Executive Owner Email' },
    ],
  },
  {
    title: 'Additional Data',
    fields: [
      { key: 'vendor_supplier', label: 'Vendor / Supplier' },
      { key: 'user_count', label: 'User Count' },
      { key: 'business_criticality', label: 'Business Criticality' },
      { key: 'technical_health_status', label: 'Technical Health Status' },
      { key: 'date_of_introduction', label: 'Date of Introduction' },
      { key: 'contract_end_date', label: 'Contract End Date' },
      { key: 'internal_number', label: 'Internal Number' },
    ],
  },
  {
    // Its own chapter, owner-directed 2026-08-24: it is the only free-text field on the
    // panel and the only one that spans both columns, so it was ending Additional Data
    // with a block that looked nothing like the fields above it.
    title: 'Description',
    fields: [
      { key: 'description', label: 'Application Description', full: true, multiline: true },
    ],
  },
];

/** Flat, because EDITABLE and anything else that asks "is this a labelled field?" wants the set, not the chapters. */
const FIELDS: DetailField[] = GROUPS.flatMap((g) => g.fields);

/**
 * A chapter heading inside the field grid. Same 10px/700 uppercase slate as the
 * "Key drivers" label further down this file — a chapter is subordinate to
 * SECTION_TITLE ("Application Details"), not a rival to it.
 */
const GROUP_TITLE_SX = {
  fontSize: 10,
  fontWeight: 700,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
} as const;

/**
 * Every labelled field is editable.
 *
 * `user_count` used to be excluded on the grounds that it was "sourced upstream". It is
 * not — nothing else writes it, so excluding it left the number permanently frozen at
 * whatever the last import set, with no way to correct it. The server validates it as a
 * whole number of users, or blank.
 */
const EDITABLE = new Set<string>(FIELDS.map((f) => f.key as string));

// The Consolidate / Cloud migration / Replace switches lived here, on the inventory
// record. In the source they are columns of `final_disposition_model` and are set on
// Final Disposition (§6.4), not on an application's detail panel. They arrive with
// backlog #6 on the screen that owns them — see docs/DEVIATIONS.md.

export interface ApplicationDetailDrawerProps {
  application: AprApplication | null;
  mode: 'view' | 'edit';
  onModeChange: (mode: 'view' | 'edit') => void;
  onClose: () => void;
  onSaved: () => void;
  /** The application's annual run rate, when it has one. */
  runRateTotal?: number | null;
  onOpenRunRate?: () => void;
  /** The parent/child group this application belongs to, in display order. */
  family?: readonly AprApplication[];
  /** Every application, for the Parent Application selector. */
  allApplications?: readonly AprApplication[];
  onSelect?: (id: string) => void;
}

export function ApplicationDetailDrawer({
  application,
  mode,
  onModeChange,
  onClose,
  onSaved,
  runRateTotal,
  onOpenRunRate,
  family = [],
  allApplications = [],
  onSelect,
}: ApplicationDetailDrawerProps) {
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
  const [saving, setSaving] = useState(false);
  /** Briefly confirms a save, since the panel no longer leaves edit mode to signal it. */
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState<string>();
  const [maximized, setMaximized] = useState(false);

  /**
   * SEEDING THE DRAFT — three rules, because one effect could not express them.
   *
   * The panel renders `draft`, not the record, so whatever seeds `draft` decides what the
   * user sees. The original single effect keyed on `[application?.id, mode]` and re-seeded on
   * BOTH, which produced a bug that looked exactly like data loss:
   *
   *   Save → the parent's reload starts (async) → mode flips to 'view' → the effect re-seeds
   *   from the record it can currently see, which is still the PRE-SAVE row. Every field just
   *   typed reverts on screen. When the reload lands the record object changes, but neither
   *   `id` nor `mode` has, so nothing re-seeds and the stale draft stays.
   *
   *   The values were saved correctly the whole time. A screen that contradicts the database
   *   is worse than one that fails outright, because the user retypes work that was never
   *   lost — or worse, believes the save is broken and stops trusting it.
   *
   * So, precisely:
   *   1. A DIFFERENT record  → seed. Nothing in the panel belongs to it yet.
   *   2. ENTERING edit       → seed. Start from what is stored, discarding any abandoned edit.
   *   3. While in VIEW       → follow the record as it changes, so a reload (including the one
   *                            the save just triggered) shows through.
   *
   * Leaving 'view' OUT of rule 2 is the fix: returning to view keeps the draft the save
   * produced instead of replacing it with a record that has not caught up yet.
   */
  useEffect(() => {
    setDraft(application ? { ...application } : null);
    setError(undefined);
    setJustSaved(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application?.id]);

  useEffect(() => {
    if (mode !== 'edit' || !application) return;
    setDraft({ ...application });
    setError(undefined);
    setJustSaved(false);
    // Contents deliberately ignored: re-seeding on every background reload would wipe an
    // edit in progress, which is the hazard the original single effect was guarding against.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, application?.id]);

  useEffect(() => {
    if (mode !== 'view' || !application) return;
    setDraft({ ...application });
    /*
     * `mode` is READ here but must NOT be a dependency.
     *
     * With it in the list, returning from edit to view re-runs this against whichever record
     * is on hand at that instant — which, straight after a save, is the pre-save one, because
     * the parent's reload has not returned. That reinstates the exact bug this effect exists
     * to fix. Depending on `application` alone means it fires when the RECORD changes, which
     * is the only moment a view-mode panel has anything new to show.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application]);

  // ── Analysis cards: the two signals that already exist server-side ──────────
  // Both are loaded per application and both fail SOFT: a card that cannot load shows the
  // same "nothing yet" state as one with no data, because a red error inside a
  // supplementary card would read as a problem with the application itself.
  const [disp, setDisp] = useState<AprDispositionSummary | null>(null);
  const [dispLoading, setDispLoading] = useState(true);
  const [licData, setLicData] = useState<AprLicensingForApp | null>(null);
  const [licLoading, setLicLoading] = useState(true);

  useEffect(() => {
    const id = application?.id;
    if (!id) return;
    let active = true;
    setDispLoading(true);
    setLicLoading(true);
    api.apr
      .getDispositionSummary(id)
      .then((d) => { if (active) setDisp(d); })
      .catch(() => { if (active) setDisp(null); })
      .finally(() => { if (active) setDispLoading(false); });
    api.apr
      .getLicensingForApp(id)
      .then((d) => { if (active) setLicData(d); })
      .catch(() => { if (active) setLicData(null); })
      .finally(() => { if (active) setLicLoading(false); });
    return () => { active = false; };
  }, [application?.id]);

  const editing = mode === 'edit';
  const readOnly = !editing;

  const set = (key: string, value: unknown) => {
    // Any keystroke means there are unsaved changes again; the "Saved" confirmation must not
    // outlive them or it says the opposite of the truth.
    setJustSaved(false);
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  };

  const save = async () => {
    if (!application || !draft) return;
    setSaving(true);
    setError(undefined);
    try {
      // `fields` covers the labelled grid only — EDITABLE is derived from FIELDS. Anything
      // rendered OUTSIDE that grid has to be named here explicitly or it is silently
      // dropped from the payload: the switch moves, the save succeeds, and the value is
      // unchanged. That is exactly how `excluded_from_apr` behaved until it was added
      // below, so keep this list in step with every control outside the grid.
      const fields = Object.fromEntries(Object.entries(draft).filter(([key]) => EDITABLE.has(key)));
      const saved = await api.apr.updateApplication(application.id, {
        ...fields,
        parent_application_id: draft.parent_application_id,
        recommendation: draft.recommendation,
        license_negotiation: Boolean(draft.license_negotiation),
        excluded_from_apr: Boolean(draft.excluded_from_apr),
      });
      // The server returns the saved row. Seeding from it means the panel is correct the
      // instant the request returns, rather than depending on the parent's reload winning a
      // race against the mode change.
      /*
       * STAY IN EDIT — owner-directed.
       *
       * Save used to return the panel to view, so a second correction meant pressing Edit
       * again and finding your place. Editing an application is rarely one field, and the
       * fields are grouped into chapters precisely because people work through them.
       *
       * The draft is seeded from the SERVER'S response rather than left as typed, so what is
       * on screen after a save is what was actually stored — a value the server trimmed,
       * rejected or normalised shows immediately instead of lingering as the text you typed.
       * `Cancel` still leaves edit mode, and `onSaved()` still refreshes the grid behind.
       */
      if (saved && typeof saved === 'object') setDraft({ ...(saved as unknown as Record<string, unknown>) });
      setJustSaved(true);
      onSaved();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  const position = useMemo(
    () => family.findIndex((a) => a.id === application?.id),
    [family, application?.id],
  );

  const step = (delta: number) => {
    const next = family[position + delta];
    if (next && onSelect) onSelect(next.id);
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(application)}
      onClose={onClose}
      // APR uses no scrim — the grid behind stays fully visible and undimmed, which
      // is what makes stepping through a family feel like staying on the screen.
      hideBackdrop
      ModalProps={{ keepMounted: false }}
      // Above the app bar. The shell's bar is raised over the navigation drawer, and
      // without this it covers the panel's top 64px — which is exactly where the
      // Back, Save, Maximize and Close controls live, leaving no way out of the panel.
      sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: maximized ? '98vw' : '54.4vw' },
          minWidth: { md: 900 },
          maxWidth: '100%',
          boxShadow: 'rgba(0, 0, 0, 0.18) -4px 0px 24px 0px',
        },
      }}
    >
      {application && draft ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header — APR: 14px/18px padding, 1px #e5e5e5 rule, and four controls.
              Outside the scrolling body, so the title is always visible. */}
          {/* TWO ROWS, not one wrapping row.
              Back / maximize / close belong in the top-right corner and must stay there,
              so they sit on row one with `flexWrap: 'nowrap'` and `flexShrink: 0`. They
              used to share one wrapping row with the family strip, and on a long name —
              "Microsoft Azure — Cloud Platform" — the row ran out of width and dropped the
              controls onto a second line under the title. The family strip now has its own
              row underneath the title it belongs to. */}
          <Box
            sx={{
              px: '18px',
              py: '14px',
              borderBottom: '1px solid',
              borderColor: '#e5e5e5',
              flexShrink: 0,
            }}
          >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ gap: 2, flexWrap: 'nowrap' }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  component="h2"
                  sx={{
                    fontSize: 17,
                    fontWeight: 600,
                    color: '#1a1a1a',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {application.name}
                </Typography>
                {application.is_parent ? <Box sx={PARENT_PILL_SX}>Parent</Box> : null}
                {application.parent_application_id ? (
                  <Box sx={CHILD_PILL_SX}>
                    Child of{' '}
                    {family.find((a) => a.id === application.parent_application_id)?.name ?? '—'}
                  </Box>
                ) : null}
              </Stack>
              <Typography sx={{ fontSize: 12, color: '#888', mt: '2px' }}>
                {application.application_id_code}
              </Typography>
            </Box>

            {/* CONTROLS. One position, always: top-right, on the title's row, for every
                application — parent, child or standalone. `flexShrink: 0` keeps them
                whole and the row is `nowrap`, so a long name ellipses instead of pushing
                them onto a line of their own. */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Box component="button" type="button" onClick={onClose} sx={BACK_BUTTON_SX}>
                ‹ Back
              </Box>
              {/* Save appears only while editing — APR has no Edit button in the
                  header; the pencil in the grid is how you get here. */}
              {editing ? (
                <Tooltip title="Save changes">
                  <Box
                    component="button"
                    type="button"
                    onClick={() => void save()}
                    disabled={saving}
                    sx={SAVE_BUTTON_SX}
                  >
                    <SaveIcon sx={{ fontSize: 20 }} />
                  </Box>
                </Tooltip>
              ) : null}
              <Tooltip title={maximized ? 'Restore' : 'Maximize'}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setMaximized((m) => !m)}
                  sx={ICON_BUTTON_SX}
                >
                  {maximized ? (
                    <CollapseIcon sx={{ fontSize: 19 }} />
                  ) : (
                    <ExpandIcon sx={{ fontSize: 19 }} />
                  )}
                </Box>
              </Tooltip>
              <Tooltip title="Close">
                <Box component="button" type="button" onClick={onClose} sx={CLOSE_BUTTON_SX}>
                  <CloseIcon sx={{ fontSize: 20 }} />
                </Box>
              </Tooltip>
            </Stack>
          </Stack>

          {/* FAMILY strip — its own row, under the title it describes. It is the one
              part of this header that varies (a standalone application has no family),
              so it goes below rather than competing with the controls for width. */}
          {family.length > 1 ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                mt: '10px',
                px: 1.5,
                py: 0.75,
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: (t) => (t.palette.mode === 'dark' ? 'action.hover' : '#f8fafc'),
                width: 'fit-content',
                maxWidth: '100%',
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6 }}>
                FAMILY
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
                {position + 1} of {family.length}
              </Typography>
              <IconButton
                size="small"
                disabled={position <= 0}
                onClick={() => step(-1)}
                aria-label="Previous in family"
              >
                <PreviousIcon fontSize="small" />
              </IconButton>
              <Select
                size="small"
                value={application.id}
                onChange={(e) => onSelect?.(String(e.target.value))}
                sx={{ minWidth: 170, maxWidth: 360, '& .MuiSelect-select': { py: '4px', fontSize: 13 } }}
              >
                {family.map((member) => (
                  <MenuItem key={member.id} value={member.id} sx={{ fontSize: 13 }}>
                    {member.name}
                    {member.is_parent ? ' (parent)' : ''}
                  </MenuItem>
                ))}
              </Select>
              <IconButton
                size="small"
                disabled={position < 0 || position >= family.length - 1}
                onClick={() => step(1)}
                aria-label="Next in family"
              >
                <NextIcon fontSize="small" />
              </IconButton>
            </Stack>
          ) : null}
          </Box>

          {saving ? <LinearProgress /> : null}
          <Divider />

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 3,
              py: 2,
              // Flex, not grid. With a grid, the family strip claimed column 1 and
              // the details pane auto-placed into the 1px rule beside it — which is
              // what collapsed the layout. A flex row cannot do that.
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: '22px',
              alignItems: 'stretch',
            }}
          >
            {/* Left column: family selector, then the details. */}
            <Stack spacing={2.5} sx={{ flex: '534 1 0', minWidth: 0 }}>
              {error ? <Alert severity="error">{error}</Alert> : null}

              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                  <Typography sx={SECTION_TITLE}>Application Details</Typography>
                  {readOnly ? (
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>
                      (read-only)
                    </Typography>
                  ) : null}
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: '12px',
                  }}
                >
                  {GROUPS.map((group, gi) => (
                    <Fragment key={group.title}>
                      {/* One grid, not one per chapter: the two columns have to keep
                          aligning straight down the panel, and a chapter that started its
                          own grid would re-measure its own columns. */}
                      <Box
                        sx={{
                          gridColumn: '1 / -1',
                          ...GROUP_TITLE_SX,
                          ...(gi === 0
                            ? {}
                            : {
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                pt: '12px',
                                mt: '4px',
                              }),
                        }}
                      >
                        {group.title}
                      </Box>
                      {group.fields.map((field) => {
                        const key = field.key as string;
                        return (
                          <Box key={key} sx={field.full ? { gridColumn: '1 / -1' } : undefined}>
                            {field.info ? labelWithInfo(field.label, field.info) : label(field.label)}
                            {readOnly || !EDITABLE.has(key) ? (
                              <ReadView value={draft[key]} />
                            ) : (
                              <TextField
                                fullWidth
                                multiline={field.multiline ?? false}
                                minRows={field.multiline ? 2 : undefined}
                                value={draft[key] ?? ''}
                                onChange={(e) => set(key, e.target.value)}
                                sx={EDIT_FIELD_SX}
                              />
                            )}
                          </Box>
                        );
                      })}
                    </Fragment>
                  ))}
                </Box>
              </Box>
            </Stack>

            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                flex: '0 0 1px',
                alignSelf: 'stretch',
                bgcolor: 'divider',
              }}
            />

            <Stack spacing={1.5} sx={{ flex: '411 1 0', minWidth: 0 }}>
              <Box>
                {/* Same SECTION_TITLE as "Application Details" and "Analysis" — this used
                    to be an 11px uppercase slate label, which read as a sub-heading inside
                    the left column rather than a section in its own right. */}
                <Typography sx={{ ...SECTION_TITLE, mb: 1.25 }}>APR Relevance</Typography>
                <Card>
                  <Stack spacing={1}>
                    {/*
                      Two flags, not one. They are routinely confused, so each states what
                      it does AND what it does not — the wording, the hints and the
                      tooltip copy are the source drawer's, verbatim.

                      "Not relevant for rationalization" is `excluded_from_apr`: 18 of the
                      579 applications carry it. It was missing from this drawer entirely
                      — and from the row type, the selected columns and the editable set
                      behind it — so there was no way to see or set it here.
                    */}
                    <FlagToggle
                      on={Boolean(draft.excluded_from_apr)}
                      disabled={readOnly}
                      onChange={(v) => set('excluded_from_apr', v)}
                      label="Not relevant for rationalization"
                      hint="Foundational platform — auto-scored 5.00 · Retain and excluded from APR models."
                      info={
                        <>
                          <p style={{ marginTop: 0 }}>
                            For <strong>foundational platforms</strong> that are never candidates for a
                            disposition decision — the application is kept as-is, so there is nothing to
                            rationalize.
                          </p>
                          <p>
                            <strong>When ON, this application:</strong>
                          </p>
                          <ul style={{ margin: '0 0 10px 18px', padding: 0 }}>
                            <li>
                              is auto-scored <strong>5.00 · Retain</strong> and cannot be selected into APR
                              models or Q&amp;A;
                            </li>
                            <li>
                              is skipped by AI Disposition Analysis — it is shown as “Not relevant for
                              disposition”.
                            </li>
                          </ul>
                          <p style={{ marginBottom: 0 }}>
                            Leave it <strong>OFF</strong> for anything you want assessed. It is about{' '}
                            <em>rationalization scope only</em> — it is not a cost or licensing setting.
                          </p>
                        </>
                      }
                    />

                    <FlagToggle
                      on={Boolean(draft.license_negotiation)}
                      disabled={readOnly}
                      onChange={(v) => set('license_negotiation', v)}
                      label="Licence-negotiation candidate"
                      hint="Track this application for licence renegotiation."
                      info={
                        <>
                          <p style={{ marginTop: 0 }}>
                            Marks the application for the <strong>Licensing Analysis</strong> module — the
                            separate licence-renegotiation workstream. It is{' '}
                            <strong>licensing-only</strong>.
                          </p>
                          <p>
                            <strong>When ON, this application:</strong>
                          </p>
                          <ul style={{ margin: '0 0 10px 18px', padding: 0 }}>
                            <li>
                              appears in the eligible list on <strong>Licensing Analysis</strong> for a
                              renegotiation run.
                            </li>
                          </ul>
                          <p style={{ marginBottom: 0 }}>
                            It does <strong>not</strong> affect Disposition Analysis. Whatever this flag
                            says, an application’s disposition still uses its{' '}
                            <strong>total cost of ownership</strong> as the basis for savings and ROI.
                          </p>
                        </>
                      }
                    />

                    {/*
                      Only shown for a licence-negotiation candidate, at the foot of this
                      section — the source does the same. It is the SAME `contract_end_date`
                      as the field in the grid above, surfaced a second time where the
                      decision that needs it is being made, not a separate value.
                    */}
                    {Boolean(draft.license_negotiation) && (
                      <Box sx={{ pt: 1.25, mt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                        {label('Contract end date (vendor)')}
                        {readOnly ? (
                          <ReadView value={draft.contract_end_date || '— Not set —'} />
                        ) : (
                          <TextField
                            type="date"
                            fullWidth
                            value={(draft.contract_end_date as string) ?? ''}
                            onChange={(e) => set('contract_end_date', e.target.value)}
                            sx={EDIT_FIELD_SX}
                          />
                        )}
                        <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5 }}>
                          The contract / renewal date with this vendor. Saved with the record.
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ pt: 1 }}>
                      {label('Parent Application')}
                      {readOnly ? (
                        <ReadView
                          value={
                            application.parent_application_id
                              ? (allApplications.find(
                                  (a) => a.id === application.parent_application_id,
                                )?.name ?? application.parent_application_id)
                              : '— None (standalone / parent) —'
                          }
                        />
                      ) : (
                        <TextField
                          select
                          fullWidth
                          value={(draft.parent_application_id as string) ?? ''}
                          onChange={(e) => set('parent_application_id', e.target.value || null)}
                          sx={EDIT_FIELD_SX}
                        >
                          <MenuItem value="">— None (standalone / parent) —</MenuItem>
                          {allApplications
                            .filter((a) => a.id !== application.id)
                            .map((a) => (
                              <MenuItem key={a.id} value={a.id} sx={{ fontSize: 13 }}>
                                {a.name} ({a.application_id_code})
                              </MenuItem>
                            ))}
                        </TextField>
                      )}
                    </Box>

                    <Box sx={{ pt: 1 }}>
                      {label('Recommendation')}
                      {readOnly ? (
                        <ReadView value={draft.recommendation} />
                      ) : (
                        <TextField
                          select
                          fullWidth
                          value={(draft.recommendation as string) ?? ''}
                          onChange={(e) => set('recommendation', e.target.value)}
                          sx={EDIT_FIELD_SX}
                        >
                          <MenuItem value="">—</MenuItem>
                          {RECOMMENDATIONS.map((value) => (
                            <MenuItem key={value} value={value}>
                              {value}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </Box>
                  </Stack>
                </Card>
              </Box>

              {/*
                The same rule that separates the two columns, laid horizontally between APR
                Relevance and Analysis now that they share this column.

                Same colour token as the vertical divider (`bgcolor: 'divider'`), but 2px
                rather than 1px — owner's call. A hairline that reads clearly as a column
                separator over the full height of the panel is too faint to register as a
                break between two stacked sections.

                `height` DOES NOT WORK here and was the first attempt: this is a flex COLUMN,
                its children default to `flex-shrink: 1`, and a thin child is the first thing
                the browser squashes to zero. The `0 0` in `flex` is grow 0 / shrink 0, which
                is what stops that — so the thickness lives in the flex BASIS.
              */}
              <Box sx={{ flex: '0 0 2px', alignSelf: 'stretch', bgcolor: 'divider' }} />

              <Typography sx={SECTION_TITLE}>Analysis</Typography>

              {/* Disposition 360 — the four signals and whether they agree.
                  Reproduced from the source drawer: convergence chip on the head, the
                  final disposition, a capsule per signal, then the threshold bar with a
                  marker for every signal that carries a score. */}
              {(() => {
                // A PARENT gets 'none'. Its body says "children carry the disposition",
                // so a green "Signals aligned" chip beside that contradicts it — and the
                // state it was reporting came from the parent's OWN model score, which is
                // exactly the signal the body is saying not to read.
                const conv = disp?.is_parent ? 'none' : (disp?.convergence?.state || 'none');
                const convMeta = CONVERGENCE_META[conv] ?? CONVERGENCE_META.none!;
                return (
                  <Card>
                    <CardHead
                      title="🧭 Disposition 360"
                      right={<DispChip label={convMeta.label} color={convMeta.color} />}
                    />
                    {dispLoading ? (
                      <Typography sx={{ fontSize: 12, color: '#999999' }}>Loading…</Typography>
                    ) : disp?.is_parent ? (
                      <Typography sx={{ fontSize: 12, color: '#999999' }}>
                        Parent rollup — children carry the disposition.
                      </Typography>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                          <span style={{ fontSize: 12, color: '#888' }}>Final:</span>
                          {disp?.final ? (
                            <DispChip
                              label={disp.final.disposition ?? disp.final.recommendation ?? '—'}
                              color={disp.final.color ?? '#6b7280'}
                              solid
                            />
                          ) : (
                            <span style={{ fontSize: 13, color: '#888', fontStyle: 'italic' }}>Not finalized</span>
                          )}
                          {disp?.final?.source && (
                            <span style={{ fontSize: 11, color: '#aaa' }}>via {disp.final.source}</span>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mb: '10px' }}>
                          <SignalMini label="Model"
                            value={disp?.model ? `${disp.model.recommendation} · ${Number(disp.model.score).toFixed(2)}` : null}
                            color={disp?.model?.color} />
                          <SignalMini label="Q&A"
                            value={disp?.qa ? (disp.qa.recommendation ?? disp.qa.status ?? null) : null}
                            color={disp?.qa?.color} muted={!disp?.qa} mutedText="not done" />
                          <SignalMini label="Suggested" value={disp?.ai?.disposition || null} color={disp?.ai?.color} />
                          <SignalMini label="Manual" value={disp?.manual?.disposition || null} color={disp?.manual?.color} />
                        </Box>
                        {disp?.thresholds && (
                          <ScoreScale
                            thresholds={disp.thresholds}
                            markers={[
                              { score: disp?.model?.score ?? null, label: 'Model' },
                              { score: disp?.qa?.score ?? null, label: 'Q&A' },
                            ]}
                          />
                        )}
                      </>
                    )}
                  </Card>
                );
              })()}

              {/* Licensing Analysis — the most recent analysis for this application.
                  A compact pointer only: the full justification, the negotiation levers
                  and the version history live on the Licensing Analysis screen, which the
                  links open filtered to this app. */}
              {(() => {
                const a = licData?.analysis;
                // A parent's OWN row is zero — the money sits on its children — so show the
                // roll-up, which is the same sum Licensing Results puts on the parent row.
                const roll = licData?.familyRollup;
                const useRollup = !!roll && Number(roll.child_count) > 0;
                const licMoney = (v: number | string | null | undefined) =>
                  `$${Math.round(Number(v) || 0).toLocaleString('en-US')}`;
                const licensingHref =
                  `/apr/license-intelligence/licensing-analysis?q=${encodeURIComponent(
                    (application.application_id_code as string) || application.name || '',
                  )}`;
                return (
                  <Card>
                    <CardHead
                      title="💷 Licensing Analysis"
                      right={
                        <a href={licensingHref}
                          style={{ fontSize: 11, color: '#4f46e5', textDecoration: 'none', fontWeight: 600 }}>
                          Open →
                        </a>
                      }
                    />
                    {licLoading ? (
                      <Typography sx={{ fontSize: 12, color: '#999999' }}>Loading…</Typography>
                    ) : !a ? (
                      <Typography sx={{ fontSize: 12, color: '#999999' }}>
                        {draft.license_negotiation
                          ? 'Flagged for licence negotiation — no analysis yet. Run it on the Licensing Analysis screen.'
                          : 'Not flagged for licence negotiation.'}
                      </Typography>
                    ) : (
                      <Box sx={{ fontSize: 12, color: '#444444' }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: '6px', flexWrap: 'wrap' }}>
                          <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, color: '#fff', background: '#4f46e5' }}>
                            {a.disposition}
                          </span>
                          <span>{a.confidence} · {a.risk} risk</span>
                        </Box>
                        {useRollup ? (
                          <>
                            <Box>
                              Current <strong>{licMoney(roll.annual_license_cost)}</strong> → Proposed{' '}
                              <strong>{licMoney(roll.new_license_mid)}</strong> · save{' '}
                              <strong style={{ color: '#16a34a' }}>{licMoney(roll.savings_mid)}</strong>
                            </Box>
                            <Box sx={{ fontSize: 11, color: '#78909c', mt: '2px' }}>
                              Family roll-up — {roll.child_count} analysed application
                              {Number(roll.child_count) === 1 ? '' : 's'}. This application's own
                              licence line is {licMoney(a.annual_license_cost)}.
                            </Box>
                          </>
                        ) : (
                          <Box>
                            Current <strong>{licMoney(a.annual_license_cost)}</strong> → Proposed{' '}
                            <strong>{licMoney(a.new_license_mid)}</strong> · save{' '}
                            <strong style={{ color: '#16a34a' }}>{licMoney(a.savings_mid)}</strong>
                          </Box>
                        )}
                        {!a.has_document && (
                          <Box sx={{ mt: '6px', color: '#b45309', fontSize: 11 }}>
                            ⚠ Estimate — no licensing document attached.
                          </Box>
                        )}
                        <a href={licensingHref}
                          style={{ display: 'inline-block', marginTop: 8, fontSize: 11, color: '#4f46e5', textDecoration: 'none', fontWeight: 600 }}>
                          View full justification, levers &amp; versions on the Licensing Analysis screen →
                        </a>
                      </Box>
                    )}
                  </Card>
                );
              })()}

              {/* Disposition Analysis — the AI result for THIS application.
                  Three states, all the source's:
                    · nothing run                → a note pointing at Run Analysis
                    · analysed in a portfolio run → the run's disposition + a link
                    · promoted to ai_initial_*    → full rationale and key drivers
                  The middle state is the one that matters most here: a completed
                  Disposition Intelligence run writes agent_run_apps, and only the separate
                  "AI disposition" action promotes it onto the application. Without this
                  card an application WITH a finished analysis showed nothing at all. */}
              {!application.is_parent && !draft.excluded_from_apr && (() => {
                const ai = disp?.ai;
                const runIdForLink = ai?.runId || disp?.latestAnalysisRunId || null;
                // Land in the Analysis Repository with this application selected — the same
                // place clicking its row there would reach, showing EVERY analysis on record
                // so the user can compare them. `from` lets closing the panel come back here.
                const analysisHref =
                  `/apr/application-evaluation/disposition-intelligence/analysis-repository` +
                  `?appId=${application.id}&from=portfolio`;
                const hasAnalysis = !!ai || !!disp?.latestAnalysisRunId;
                return (
                  <Card>
                    <CardHead
                      title="🧠 Disposition Analysis"
                      right={runIdForLink ? (
                        <a href={analysisHref} title="Open this application's AI disposition analysis"
                          style={{ fontSize: 11, color: '#4f46e5', textDecoration: 'none', fontWeight: 600 }}>Open →</a>
                      ) : undefined}
                    />
                    {dispLoading ? (
                      <Typography sx={{ fontSize: 12, color: '#999999' }}>Loading…</Typography>
                    ) : !hasAnalysis ? (
                      <Typography sx={{ fontSize: 12, color: '#999999' }}>
                        No AI disposition yet. Run a portfolio analysis from Disposition
                        Intelligence → Run Analysis; results appear on the Analysis Results screen.
                      </Typography>
                    ) : !ai ? (
                      <>
                        {disp?.latestAnalysisDisposition && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 12, color: '#888' }}>AI-suggested:</span>
                            <DispChip label={disp.latestAnalysisDisposition} color="#4f46e5" solid />
                          </Box>
                        )}
                        <Box sx={{ fontSize: 12.5, color: '#334155', lineHeight: 1.5 }}>
                          This application was analyzed in a portfolio run. Open its full
                          disposition, savings and rationale on the Analysis Results screen.
                        </Box>
                        <Box sx={{ textAlign: 'right', mt: '10px' }}>
                          <a href={analysisHref}
                            style={{ fontSize: 12, color: '#4f46e5', textDecoration: 'none', fontWeight: 600 }}>
                            View full analysis for this application →
                          </a>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, color: '#888' }}>AI-suggested:</span>
                          <DispChip label={ai.disposition ?? '—'} color={ai.color ?? '#4f46e5'} solid />
                          {ai.confidence && (
                            <DispChip label={`${ai.confidence} confidence`}
                              color={ai.confidence === 'High' ? '#1b7a2b' : ai.confidence === 'Low' ? '#b45309' : '#2563eb'} />
                          )}
                        </Box>
                        {ai.rationale && (
                          <Box sx={{ fontSize: 12.5, color: '#334155', lineHeight: 1.5, mb: ai.drivers?.length ? '10px' : 0 }}>
                            {ai.rationale}
                          </Box>
                        )}
                        {!!ai.drivers?.length && (
                          <Box>
                            <Box sx={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', mb: '6px' }}>
                              Key drivers
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {ai.drivers.map((d: string, i: number) => (
                                <span key={i} style={{ fontSize: 11, color: '#3730a3', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 12, padding: '3px 9px' }}>{d}</span>
                              ))}
                            </Box>
                          </Box>
                        )}
                        <Box sx={{ textAlign: 'right', mt: '10px' }}>
                          <a href={analysisHref}
                            style={{ fontSize: 12, color: '#4f46e5', textDecoration: 'none', fontWeight: 600 }}>
                            View full analysis for this application →
                          </a>
                        </Box>
                      </>
                    )}
                  </Card>
                );
              })()}

              {/* The source's rightPane gates three of its six cards:
                    {!isParent && !excluded && dispositionAnalysisCard}
                    {!excluded && costCard}
                    {benefitCard}
                    {!excluded && roadmapCard}
                  An application marked "Not relevant for rationalization" is not going to
                  be re-platformed, so its disposition cost and its roadmap slot are not
                  questions anyone is asking. Benefit Realization stays: a foundational
                  platform still carries benefit. Without this the platform showed five
                  cards where the source showed three. */}
              {!draft.excluded_from_apr && (
              <Card>
                <CardHead
                  title="📊 Disposition Cost Analysis"
                  right={
                    runRateTotal ? (
                      <Chip
                        size="small"
                        label={`${formatCurrency(runRateTotal)} / yr`}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    ) : undefined
                  }
                />
                <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                  {runRateTotal
                    ? 'Current run rate is recorded. One-time disposition costs are calculated in Program Planning & Roadmap → Business Case.'
                    : 'No cost record for this application yet.'}
                </Typography>
                {onOpenRunRate ? (
                  <Box sx={{ textAlign: 'right', mt: 1 }}>
                    <Button size="small" onClick={onOpenRunRate} sx={{ fontSize: 12 }}>
                      Open Current Run Rate →
                    </Button>
                  </Box>
                ) : null}
              </Card>
              )}

              <AwaitingCard
                title="📈 Benefit Realization"
                note="Calculated in Program Planning & Roadmap → Business Case → Benefit Realization."
              />
              {!draft.excluded_from_apr && (
                <AwaitingCard
                  title="🗺️ HL Implementation Road Map"
                  note="Not scheduled on the implementation roadmap yet."
                />
              )}
            </Stack>
          </Box>

          {editing ? (
            <>
              <Divider />
              <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ px: 3, py: 2 }}>
                <Button onClick={() => onModeChange('view')} disabled={saving}>
                  {justSaved ? 'Done' : 'Cancel'}
                </Button>
                <Button variant="contained" onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : justSaved ? 'Saved ✓' : 'Save'}
                </Button>
              </Stack>
            </>
          ) : null}
        </Box>
      ) : null}
    </Drawer>
  );
}
